import Cart from 'cart-store';

function voucherCodes(cart) {
  const applications = [
    ...(cart.cart_level_discount_applications || []),
    ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application)),
  ];
  const codes = [
    ...(cart.discount_codes || []).map(discount => discount.code),
    ...applications.filter(discount => discount?.type === 'discount_code').map(discount => discount.title),
  ];
  return [...new Map(codes.filter(Boolean).map(code => [code.toLowerCase(), code])).values()];
}

class BigDropCartVoucher extends HTMLElement {
  connectedCallback() {
    if (this.initialized) {
      this.unsubscribe = Cart.subscribe(state => this.renderCodes(state.cart));
      this.renderCodes(Cart.getState().cart);
      return;
    }
    this.initialized = true;
    this.input = this.querySelector('input');
    this.applyButton = this.querySelector('[data-apply]');
    this.status = this.querySelector('[role="status"]');
    this.list = this.querySelector('[data-codes]');
    this.applyButton.addEventListener('click', () => this.apply());
    this.input.addEventListener('keydown', event => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      event.stopPropagation();
      this.apply();
    });
    this.unsubscribe = Cart.subscribe(state => this.renderCodes(state.cart));
    this.renderCodes(Cart.getState().cart);
    this.hidden = false;
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  renderCodes(cart) {
    this.codes = voucherCodes(cart);
    this.list.replaceChildren(...this.codes.map(code => {
      const item = document.createElement('li');
      const title = document.createElement('span');
      title.textContent = code;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove voucher ${code}`);
      remove.disabled = Boolean(this.busy);
      remove.addEventListener('click', () => this.update(this.codes.filter(value => value !== code)));
      item.append(title, remove);
      return item;
    }));
    this.list.hidden = !this.codes.length;
  }

  apply() {
    if (this.busy) return;
    const code = this.input.value.trim();
    if (!code || code.includes(',')) {
      this.status.textContent = 'Enter one voucher code at a time.';
      this.input.focus();
      return;
    }
    const codes = this.codes.filter(value => value.toLowerCase() !== code.toLowerCase());
    this.update([...codes, code], code);
  }

  async update(codes, addedCode) {
    if (this.busy) return;
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    this.querySelectorAll('button, input').forEach(control => { control.disabled = true; });
    this.status.textContent = 'Updating voucher…';
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      if (!response.ok) throw new Error('Unable to update this voucher. Please try again.');
      const updated = await response.json();
      await Cart.refresh();
      const discount = updated.discount_codes?.find(value => value.code.toLowerCase() === addedCode?.toLowerCase());
      if (!addedCode) {
        this.status.textContent = 'Voucher removed.';
      } else if (discount?.applicable === false) {
        this.status.textContent = 'This voucher cannot be applied to your current cart. Check the code and its terms.';
      } else if (discount?.applicable === true || voucherCodes(updated).some(code => code.toLowerCase() === addedCode.toLowerCase())) {
        this.status.textContent = 'Voucher applied.';
        this.input.value = '';
      } else {
        this.status.textContent = 'No cart discount applied. Check the code and its terms; delivery discounts are confirmed at checkout.';
      }
    } catch {
      this.status.textContent = 'Unable to update this voucher. Please try again.';
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.querySelectorAll('button, input').forEach(control => { control.disabled = false; });
    }
  }
}

customElements.define('bigdrop-cart-voucher', BigDropCartVoucher);
